import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { isNil } from 'lodash-es';
import { SelectionChangedEvent } from 'ag-grid-community';

import { Box, FileSelectorFileType, Modal, ModalFooter, ModalHeader, RadioGroup, Select, BodySmall, SelectChangeEvent, BodyXS } from '@parspec/pixel';

import { useCopyDataFromExistingBomMutation, useUploadDifferentFile } from './queries';
import { useUserAccountPreferencesQuery } from '../../Settings/MyProfile/queries';
import { useDeleteBomDataMutation, useGetBomSectionsQuery, useOptionSectionQuery } from '../queries';
import { useDatasheetAutoSearchMutation, useOmAutoSearchMutation } from '../queries';
import ExistingBomOptionFlow from './ExistingBomOptionFlow';
import { environment } from 'src/environments';
import {
    AutoCompleteOption,
    EXISTING_BOM_OPTION,
    IMPORT_OPTIONS,
    SCHEDULE,
    SCHEDULE_ALLOWED_FILE_TYPES,
    SUBMITTAL_PACKAGE_ALLOWED_FILE_TYPES,
} from './shared/constants';
import { REQUIRED_FIELD } from '../../shared/constants';
import FilesTabsWrapper from '../shared/uploadFileModal/FileTabs';
import { useUpdateAddFilesForProject } from 'src/features/Project/queries';
import { type ProjectFileRow } from 'src/features/shared/ProjectsTable/types';
import { BulkUploadFilesRequest } from 'src/features/Project/queries/apiTypes';
import { UploadedFilePath } from '../shared/types';
import { PARSEC_EXCEL_IMPORT_WARNING } from '../shared/constants';
import useGetEmptyBods from '../shared/hooks/useGetEmptyBods';
import { isRecognisedFileFormat } from '../shared/utils';
import { BodResponse } from '../queries/apiTypes';

interface IFileType {
    file: FileSelectorFileType;
    s3_file_path?: string;
}

interface ImportMoreProductsProps {
    open: boolean;
    onCloseClick: () => void;
    onSuccess: (res: any, packageType: any) => void;
    onCopyExistingBomSuccess?: () => void;
    enableMainSections?: boolean;
    enableOptionSections?: boolean;
}

export const ImportMoreProducts = (props: ImportMoreProductsProps) => {
    const { bomId, projectId } = useParams();
    const {
        open,
        onCloseClick,
        onSuccess,
        enableMainSections = true,
        enableOptionSections = true,
    } = props;

    const { isLoading: isUploadFileLoading, mutateAsync: uploadFile } = useUploadDifferentFile();
    const { data: userPreferences } = useUserAccountPreferencesQuery();
    const { isLoading: isAutoSearchDatasheetLoading } = useDatasheetAutoSearchMutation();
    const { mutateAsync: addProjecFiles, isLoading: projectAddLoading } = useUpdateAddFilesForProject();
    const { isLoading: isAutoSearchOMLoading } = useOmAutoSearchMutation();
    const [selectedProject, setSelectedProject] = useState<{ id: number; name: string }>();
    const [selectedBom, setSelectedBom] = useState<{ id: number; name: string }>();
    const [bomError, setBomError] = useState('');
    const [overWriteTaxes, setOverWriteTaxes] = useState(true);
    //default value should be first option
    const [selectedSection, setSelectedSection] = useState<string | number>('');
    const [uploadedFilePath, setUploadedFilePath] = useState<UploadedFilePath | null>(null);
    const [uploadedFile, setUploadedFile] = useState<IFileType[] | null>();

    const [fileRequiredError, setFileRequiredError] = useState('');

    const [radioInputValue, setRadioInputValue] = useState(SCHEDULE);

    const isExistingBomOptionSelected = radioInputValue === EXISTING_BOM_OPTION;
    const { data: sectionData } = useGetBomSectionsQuery(Number(bomId), {
        enabled: Boolean(bomId)
    });

    const { data: optionSectionData } = useOptionSectionQuery(Number(bomId), {
        enabled: Boolean(bomId)
    });

    const { mutateAsync: deleteBods, isLoading: isDeletingBods } = useDeleteBomDataMutation();

    const { mutateAsync: copyDataFromExistingBom, isLoading: existingBomMutationLoading } = useCopyDataFromExistingBomMutation();

    const onSelectLocation = () => {
        setSelectedProject(undefined);
        setSelectedBom(undefined);
        setBomError('');
    };
    const onSelectProject = (project: AutoCompleteOption) => {
        setSelectedProject(project);
        setSelectedBom(undefined);
    };

    const onSelectBom = (bom: AutoCompleteOption) => {
        setSelectedBom(bom);
        setBomError('');
    };

    const onToggleOverWriteTaxes = (value: boolean) => setOverWriteTaxes(value);

    const optionSectionObj = useMemo(() => {
        if (!optionSectionData?.data) {
            return new Set<number>();
        }

        return optionSectionData?.data.reduce((optionsectionObj, sectionData) => optionsectionObj.add(sectionData.id), new Set<number>());
    }, [optionSectionData?.data]);

    const sectionId = selectedSection !== '' ? Number(selectedSection) : null;
    const sectionType = !isNil(sectionId) && optionSectionObj.has(sectionId)
        ? "substitute_section" : "section";

    const { isLoading: isLoadingBomData, emptyBods } = useGetEmptyBods(
        bomId!, sectionId, false, sectionType, "single"
    );

    const sectionsDropdownOptions = useMemo(() => {
        const allSectionsData = [
            ...(enableMainSections ? sectionData?.data || [] : []),
            ...(enableOptionSections ? optionSectionData?.data || [] : [])
        ];
        return allSectionsData.map((item) => ({ name: item.name, id: item.id }));
    }, [sectionData?.data, optionSectionData?.data, enableMainSections, enableOptionSections]);

    useEffect(() => {
        if (sectionData?.data?.length && enableMainSections) {
            setSelectedSection(prev => prev || sectionData.data[0].id);
        } else if (optionSectionData?.data.length && enableOptionSections) {
            setSelectedSection(prev => prev || optionSectionData.data[0].id);
        }
    }, [sectionData?.data, optionSectionData?.data, enableMainSections, enableOptionSections]);

    const radioChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.value === IMPORT_OPTIONS[2].value) {
            setFileRequiredError('')
        }
        setRadioInputValue(event.target.value);
    };
    const handleExistinBomSubmit = async () => {
        if (!selectedBom) {
            return setBomError(REQUIRED_FIELD);
        }

        const input = {
            bomId: Number(bomId),
            source_bom_id: selectedBom.id,
            overwrite_taxes_and_freight: overWriteTaxes
        };
        await copyDataFromExistingBom(input);
        props.onCopyExistingBomSuccess?.();
    };

    const deleteBodSafely = async (emptyBod: BodResponse) => {
        // If deleting an empty bod fails, we shouldn't throw an error or show an error message.
        // The main action(uploadFile) has already been passed,
        // so a mutation failure in deleting the bod, will not affect the main action.
        // Showing an error message in this case will only create confusion,
        // as the new bods will still be present on bom details page, since the main action has succeeded.
        try {
            await deleteBods({ bomId: String(bomId), ids: [emptyBod.id] });
        } catch (err) { }
    };

    const handleSubmit = async () => {
        if (isExistingBomOptionSelected) {
            return handleExistinBomSubmit();
        }
        if (!uploadedFilePath?.path) {
            setFileRequiredError('A file is required for the option selected.');
            return;
        } else {
            const formData = new FormData();
            formData.append('file_info', uploadedFilePath?.path);
            if (
                (enableMainSections && sectionData?.data?.length)
                || (enableOptionSections && optionSectionData?.data.length)
            ) {
                formData.append('section_id', String(selectedSection));
                if (optionSectionObj.has(Number(selectedSection))) {
                    formData.append('section_type', 'substitute');
                } else {
                    formData.append('section_type', 'primary');
                }
            }
            // bomId will be passed by developer from route parameter.
            try {
                const res = await uploadFile({
                    packageType: radioInputValue,
                    bom_id: Number(bomId),
                    data: formData
                });
                if (uploadedFile && uploadedFile.length > 0
                ) {
                    try {
                        const newBulkUploadFilesRequest: BulkUploadFilesRequest[] = [{
                            filename: uploadedFile[0].file.name.substring(0, uploadedFile[0].file.name.lastIndexOf('.')),
                            filetype: uploadedFile[0].file.name.substring(uploadedFile[0].file.name.lastIndexOf('.') + 1),
                            file_size: uploadedFile[0].file.size || 0,
                            s3_url: environment.j + uploadedFile[0].s3_file_path
                        }];

                        await addProjecFiles({ projectId: Number(projectId), payload: newBulkUploadFilesRequest });
                    } catch (e: any) {
                        onSuccess(res.data, radioInputValue);
                        return;
                    }
                }

                const [emptyBod] = emptyBods;
                if (isRecognisedFileFormat(res.data) && emptyBod) {
                    await deleteBodSafely(emptyBod);
                }

                onSuccess(res.data, radioInputValue);
            } catch (e: any) {
                setFileRequiredError(e?.response?.data?.message);
            }
        }

    };
    const header = <ModalHeader title="Import Products" onClose={onCloseClick} />;
    const footer = (
        <ModalFooter
            onReject={() => {

                onCloseClick()
                setFileRequiredError('')
            }}
            onAccept={handleSubmit}
            continueButtonLabel="Confirm"
            helperText={<BodyXS width="380px" color="error">{fileRequiredError}</BodyXS>}
            isLoading={existingBomMutationLoading || projectAddLoading || (userPreferences?.data.autoselection ? isUploadFileLoading || isAutoSearchDatasheetLoading || isAutoSearchOMLoading : isUploadFileLoading) || isDeletingBods || isLoadingBomData}
        />
    );
    const onSelect = (files: FileSelectorFileType[]) => {
        if (!files.length) {
            setUploadedFile?.(null);
        }
        setFileRequiredError?.('');
    };
    const onUpload = (files: IFileType[]) => {
        if (files.length) {
            setUploadedFilePath?.({ name: files[0].file.name || '', path: files[0].s3_file_path || '' });
            setUploadedFile?.(files);
            setFileRequiredError?.('');
            return;
        }
        setFileRequiredError?.('');
        setUploadedFilePath?.(null);
        setUploadedFile?.(null);
    };
    const onSelectionChanged = useCallback((param: SelectionChangedEvent<ProjectFileRow>) => {
        // Only files will be selected, get the first one(Ideally there'll be only one)
        const [selectedRow] = param.api.getSelectedRows();

        if (selectedRow) {
            setUploadedFilePath({
                name: selectedRow.file_name,
                path: selectedRow.s3_url,
                size: selectedRow.file_size
            })
        } else setUploadedFilePath(null);

        setFileRequiredError?.('')
    }, []);

    return (
        <Modal header={header} footer={footer} open={open}>
            <form>
                <Box width="550px" position="relative" height={'100%'}>
                    {isUploadFileLoading && (
                        <Box width="100%" height="100%" display="flex" alignItems="center" justifyContent="center" position="absolute" sx={{ bgcolor: 'rgba(255,255,255,0.5)' }} zIndex={200} />
                    )}

                    <Box>
                        <RadioGroup
                            name=""
                            options={IMPORT_OPTIONS}
                            label="How would you like to import products?"
                            onChange={radioChanged}
                            value={radioInputValue}
                        />
                    </Box>
                    {!isExistingBomOptionSelected ? (
                        <>
                            {sectionsDropdownOptions.length ? (
                                <Box mt={2}>
                                    <BodySmall gutterBottom color="text.secondary">
                                        Select a section to import products to:
                                    </BodySmall>
                                    <Select
                                        label=""
                                        onChange={(e: SelectChangeEvent<string>) => setSelectedSection(e.target.value)}
                                        options={sectionsDropdownOptions}
                                        optionLabelKeyname="name"
                                        optionValueKeyname="id"
                                        value={selectedSection}
                                    />
                                </Box>
                            ) : null}
                        </>
                    ) : (
                        <ExistingBomOptionFlow
                            selectedProject={selectedProject}
                            onSelectLocation={onSelectLocation}
                            onSelectProject={onSelectProject}
                            selectedBom={selectedBom}
                            onSelectBom={onSelectBom}
                            bomError={bomError}
                            overWriteTaxes={overWriteTaxes}
                            onToggleOverWriteTaxes={onToggleOverWriteTaxes}
                        />
                    )}
                    <Box display={isExistingBomOptionSelected ? "none" : "block"} mt={4}>
                        <BodySmall fontWeight={500}>Select Source</BodySmall>
                        <FilesTabsWrapper
                            radioInputValue={radioInputValue}
                            onSelect={onSelect}
                            onUpload={onUpload}
                            setUploadedFilePath={setUploadedFilePath}
                            setUploadedFile={setUploadedFile}
                            fileUploadWarning={PARSEC_EXCEL_IMPORT_WARNING}
                            uploadedFile={uploadedFile}
                            acceptedFileTypes={radioInputValue === SCHEDULE ? SCHEDULE_ALLOWED_FILE_TYPES : SUBMITTAL_PACKAGE_ALLOWED_FILE_TYPES}
                            onSelectionChanged={onSelectionChanged}
                        />
                    </Box>
                </Box>
            </form>
        </Modal>
    );
};
